import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { PaymentProviderDialog } from '../components/PaymentProviderDialog';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Check, Star, HandHeart, Brain, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubscriptionsPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading] = useState(false);
  const [error] = useState('');
  const [physioPackages, setPhysioPackages] = useState([]);
  const [psyPackages, setPsyPackages] = useState([]);
  // { id, kind, name_ar, name_en, amount }
  const [dialogPkg, setDialogPkg] = useState(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/subscriptions/packages`);
        setPackages(response.data);
      } catch (err) {
        console.error('Error fetching packages:', err);
      }
    };

    const fetchMySubscription = async () => {
      try {
        const response = await axios.get(`${API}/subscriptions/my-subscription`, { withCredentials: true });
        if (response.data.active) setCurrentSubscription(response.data.subscription);
      } catch (_err) {
        // Not subscribed
      }
    };

    fetchPackages();
    // Physio + psychology packages live on this page too so patients can see
    // everything Tababa offers in one place.
    axios.get(`${API}/physio/packages`).then((r) => setPhysioPackages(r.data || [])).catch(() => {});
    axios.get(`${API}/psychology/packages`).then((r) => setPsyPackages(r.data || [])).catch(() => {});
    if (user) fetchMySubscription();
  }, [user]);

  const openCheckout = (pkg) => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent('/subscriptions')}`);
      return;
    }
    setDialogPkg(pkg);
  };

  const handleSubscribe = (pkg) => openCheckout({
    id: pkg.id, kind: 'subscription',
    name_ar: pkg.name_ar, name_en: pkg.name_en, amount: pkg.price,
  });

  const handlePhysio = (pkg) => openCheckout({
    id: pkg.id, kind: 'physio',
    name_ar: pkg.title_ar, name_en: pkg.title_en,
    amount: pkg.offer_price_usd || pkg.price_usd,
  });

  const handlePsychology = (pkg) => openCheckout({
    id: pkg.id, kind: 'psychology',
    name_ar: pkg.name_ar, name_en: pkg.name_en, amount: pkg.price,
  });

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#2c3e50] mb-3" data-testid="subscriptions-page-title">
            {t('باقات الاشتراك', 'Subscription Packages')}
          </h1>
          <p className="text-gray-600 text-lg">
            {t('اختر الباقة التي تناسب احتياجاتك', 'Choose the package that suits your needs')}
          </p>
        </div>

        {currentSubscription && (
          <Alert className="mb-8 max-w-3xl mx-auto bg-green-50 border-green-200">
            <Check className="h-5 w-5 text-green-600" />
            <AlertDescription className="text-green-800">
              {t('اشتراكك الحالي', 'Current subscription')}: {language === 'ar' ? currentSubscription.package_name_ar : currentSubscription.package_name_en}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" className="mb-6 max-w-3xl mx-auto">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto justify-items-center" data-testid="packages-grid">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`p-8 relative w-full max-w-md ${packages.length === 1 ? 'md:col-start-2' : ''} ${pkg.popular ? 'border-2 border-[#1e88e5] shadow-xl md:scale-105' : ''}`}
              data-testid={`package-${pkg.id}`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#1e88e5] text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                  <Star size={14} fill="white" />
                  {t('الأكثر شعبية', 'Most Popular')}
                </div>
              )}

              <h3 className="text-2xl font-bold text-[#2c3e50] mb-2">
                {language === 'ar' ? pkg.name_ar : pkg.name_en}
              </h3>

              <div className="mb-6">
                {pkg.original_price && pkg.original_price > pkg.price && (
                  <span className="text-2xl text-gray-400 line-through me-2 align-middle">
                    ${pkg.original_price}
                  </span>
                )}
                <span className="text-5xl font-bold text-[#1e88e5]">${pkg.price}</span>
                <span className="text-gray-600">/{t('شهر', 'month')}</span>
                {pkg.original_price && pkg.original_price > pkg.price && (
                  <div className="mt-1 inline-block bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
                    {t(
                      `وفّر $${pkg.original_price - pkg.price}`,
                      `Save $${pkg.original_price - pkg.price}`
                    )}
                  </div>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {(language === 'ar' ? pkg.features_ar : pkg.features_en).map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleSubscribe(pkg)}
                disabled={loading}
                className={`w-full ${
                  pkg.popular
                    ? 'bg-[#1e88e5] hover:bg-[#1976d2]'
                    : 'bg-[#2c3e50] hover:bg-[#1c2e40]'
                }`}
                data-testid={`subscribe-${pkg.id}`}
              >
                {loading ? t('جاري...', 'Loading...') : t('اشترك الآن', 'Subscribe Now')}
              </Button>
            </Card>
          ))}
        </div>

        {/* ---------------- Physiotherapy packages ---------------- */}
        {physioPackages.length > 0 && (
          <section className="max-w-6xl mx-auto mt-20" data-testid="physio-packages-section">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-11 h-11 rounded-2xl bg-emerald-500 text-white flex items-center justify-center">
                <HandHeart size={22} />
              </span>
              <div>
                <h2 className="text-2xl font-bold text-[#2c3e50]">
                  {t('باقات العلاج الفيزيائي', 'Physiotherapy Packages')}
                </h2>
                <p className="text-sm text-gray-600">
                  {t('جلسات علاج فيزيائي في منزلك — ادفع ثم حدّد موعدك',
                     'Home physiotherapy sessions — pay, then pick your appointment')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              {physioPackages.map((pkg) => {
                const hasOffer = pkg.offer_price_usd && pkg.offer_price_usd < pkg.price_usd;
                return (
                  <Card key={pkg.id} className="p-6 flex flex-col" data-testid={`subs-physio-${pkg.id}`}>
                    <h3 className="text-lg font-bold text-[#2c3e50] mb-3 min-h-[56px]">
                      {language === 'ar' ? pkg.title_ar : pkg.title_en}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {t(`${pkg.sessions} جلسة`, `${pkg.sessions} sessions`)}
                    </p>
                    <div className="mb-6">
                      {hasOffer && (
                        <span className="text-lg text-gray-400 line-through me-2">${pkg.price_usd}</span>
                      )}
                      <span className="text-4xl font-extrabold text-emerald-600">
                        ${hasOffer ? pkg.offer_price_usd : pkg.price_usd}
                      </span>
                    </div>
                    <Button
                      onClick={() => handlePhysio(pkg)}
                      className="w-full mt-auto bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`subs-physio-buy-${pkg.id}`}
                    >
                      {t('احجز الباقة', 'Get this package')}
                      <ArrowRight size={16} className="ms-2" />
                    </Button>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* ---------------- Psychology packages ---------------- */}
        {psyPackages.length > 0 && (
          <section className="max-w-6xl mx-auto mt-20" data-testid="psychology-packages-section">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-11 h-11 rounded-2xl bg-indigo-500 text-white flex items-center justify-center">
                <Brain size={22} />
              </span>
              <div>
                <h2 className="text-2xl font-bold text-[#2c3e50]">
                  {t('باقات الدعم النفسي', 'Psychology Packages')}
                </h2>
                <p className="text-sm text-gray-600">
                  {t('جلسات مع أخصائي نفسي — ادفع ثم اختر طبيبك وموعدك',
                     'Sessions with a psychologist — pay, then choose your doctor and time')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              {psyPackages.map((pkg) => (
                <Card
                  key={pkg.id}
                  className={`p-6 flex flex-col relative ${pkg.popular ? 'border-2 border-indigo-400 shadow-lg' : ''}`}
                  data-testid={`subs-psy-${pkg.id}`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      {t('الأكثر اختياراً', 'Most chosen')}
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-[#2c3e50] mb-2">
                    {language === 'ar' ? pkg.name_ar : pkg.name_en}
                  </h3>
                  <div className="mb-4">
                    <span className="text-4xl font-extrabold text-indigo-600">${pkg.price}</span>
                    <span className="text-gray-500 text-sm ms-1">
                      {language === 'ar' ? pkg.billing_label_ar : pkg.billing_label_en}
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-gray-500 mb-4">
                    {t(
                      `${pkg.sessions_included} جلسة · ${pkg.session_minutes || 30} دقيقة للجلسة`,
                      `${pkg.sessions_included} session(s) · ${pkg.session_minutes || 30} min each`
                    )}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {(language === 'ar' ? pkg.features_ar : pkg.features_en).slice(0, 3).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check size={16} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handlePsychology(pkg)}
                    className="w-full mt-auto bg-indigo-600 hover:bg-indigo-700"
                    data-testid={`subs-psy-buy-${pkg.id}`}
                  >
                    {t('احجز الباقة', 'Get this package')}
                    <ArrowRight size={16} className="ms-2" />
                  </Button>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>

      <PaymentProviderDialog
        open={!!dialogPkg}
        onOpenChange={(v) => { if (!v) setDialogPkg(null); }}
        packageId={dialogPkg?.id}
        packageKind={dialogPkg?.kind || 'subscription'}
        packageNameEn={dialogPkg?.name_en}
        packageNameAr={dialogPkg?.name_ar}
        amount={dialogPkg?.amount}
      />
    </div>
  );
};
